<template>
    <div v-show="!showDocEdit">
        <div class="proc-page">
            <div v-if="isLoading" class="loading-overlay">
                <p>Carregando...</p>
            </div>

            <div v-if="msgAlerta" class="info-alert proc-alert" @click="msgAlerta = null">
                {{ msgAlerta }}
            </div>

            <ProcEditHeader
                :processo="objProcesso"
                :etapa="objEtapa"
                :perfil="objPerfil"
                :etapa-anterior="etapaAnterior"
                :etapa-proxima="etapaProxima"
                :resumo="resumoEtapa"
            />

            <main
                class="proc-content"
                :class="{
                    'proc-content--expanded': !!colunaExpandida,
                    [`proc-content--cols-${qtdColunasVisiveis}`]: true,
                }"
            >
                <ProcAnexosColumn
                    v-if="exibirColunaAnexos"
                    v-show="colunaVisivel('anexos')"
                    :anexos="lsAnexos"
                    :resumo="resumoEtapa"
                    :pode-anexar="podeAnexar"
                    :expanded="colunaExpandida === 'anexos'"
                    :status-anexo-texto="statusAnexoTexto"
                    :status-anexo-classe="statusAnexoClasse"
                    :pode-adicionar-anexo="podeAdicionarAnexo"
                    :formatar-data-hora="formatarDataHora"
                    :formatar-tamanho="formatarTamanho"
                    :hash-resumido="hashResumido"
                    @abrir-upload="abrirUpload"
                    @visualizar-anexo="visualizarAnexo"
                    @remover="removerAnexo"
                    @toggle-expand="alternarColuna('anexos')"
                />

                <ProcFormulariosColumn
                    v-if="exibirColunaFormularios"
                    v-show="colunaVisivel('formularios')"
                    :formularios="lsFormularios"
                    :resumo="resumoEtapa"
                    :pode-inserir-dados="podeInserirDados"
                    :expanded="colunaExpandida === 'formularios'"
                    :status-doc-texto="statusDocTexto"
                    :status-doc-classe="statusDocClasse"
                    @editar="editarDocumento"
                    @cancelar="cancelarDocumento"
                    @visualizar-anexo="visualizarAnexo"
                    @toggle-expand="alternarColuna('formularios')"
                />

                <ProcDocumentosColumn
                    v-if="exibirColunaDocumentos"
                    v-show="colunaVisivel('documentos')"
                    :documentos="lsDocumentos"
                    :pode-analisar="podeAnalisarDocumentos"
                    :expanded="colunaExpandida === 'documentos'"
                    :formatar-data-hora="formatarDataHora"
                    :status-analise-texto="statusAnaliseTexto"
                    :status-analise-classe="statusAnaliseClasse"
                    @visualizar-anexo="visualizarAnexo"
                    @analisar="abrirAnaliseDocumento"
                    @toggle-expand="alternarColuna('documentos')"
                />
            </main>

            <footer class="proc-footer">
                <button class="btn-small proc-btn" type="button" @click="closeForm">Voltar</button>

                <div class="footer-actions">
                    <button
                        v-if="podeRetornar"
                        class="btn-small proc-btn warning"
                        type="button"
                        @click="retornarEtapa"
                    >
                        Retornar
                    </button>

                    <button
                        v-if="podeAvancar"
                        class="btn-small proc-btn primary"
                        type="button"
                        @click="avancarEtapa"
                    >
                        Avançar
                    </button>

                    <button
                        v-if="podeFinalizar"
                        class="btn-small proc-btn primary"
                        type="button"
                        @click="avancarEtapa"
                    >
                        Finalizar
                    </button>
                </div>
            </footer>
        </div>
    </div>

    <div v-if="showDocEdit">
        <DocEditPage :idEdicao="idDocEdicao" @close="showDocEdit = false" @save="lerDados" />
    </div>

    <ProcAnexoUploadModal
        ref="uploadModalRef"
        :anexo="anexoUploadAtual"
        v-model:nome="edNomeDoc"
        v-model:validade="edValidade"
        v-model:info="edInfo"
        :msg-alerta="msgAlertaForm"
        @salvar="salvarAnexo"
        @fechar="fecharUpload"
        @limpar-alerta="msgAlertaForm = null"
    />

    <ProcAnaliseDocumentoModal
        :documento="documentoAnaliseAtual"
        @salvar="salvarAnaliseDocumento"
        @fechar="fecharAnaliseDocumento"
    />

    <ConfirmModal ref="confirmModal" />
    <ModalJustificativa ref="modalJustificativa" />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { globalStore } from '@/globals';
import { Validar } from '@/funcoes/validacoes';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import DocEditPage from '@/views/docs/formularios/DocEditPage.vue';
import ModalJustificativa from '@/components/modals/ModalJustificativa.vue';

import ProcEditHeader from './ProcEditHeader.vue';
import ProcAnexosColumn from './ProcAnexosColumn.vue';
import ProcFormulariosColumn from './ProcFormulariosColumn.vue';
import ProcDocumentosColumn from './ProcDocumentosColumn.vue';
import ProcAnexoUploadModal from './ProcAnexoUploadModal.vue';
import ProcAnaliseDocumentoModal from './ProcAnaliseDocumentoModal.vue';

const emit = defineEmits(['close', 'save']);
const props = defineProps({
    idEdicao: { type: Number, default: 0 },
});

const confirmModal = ref(null);
const modalJustificativa = ref(null);

const idDocEdicao = ref(0);
const isLoading = ref(false);
const msgAlerta = ref(null);
const msgAlertaForm = ref(null);

const objPerfil = ref(null);
const objProcesso = ref(null);
const objEtapa = ref(null);

const lsAnexos = ref([]);
const lsDocumentos = ref([]);
const lsFormularios = ref([]);

const showDocEdit = ref(false);
const uploadModalRef = ref(null);
const anexoUploadAtual = ref(null);
const documentoAnaliseAtual = ref(null);
const colunaExpandida = ref(null);

const etapaAnterior = ref(null);
const etapaProxima = ref(null);

const edNomeDoc = ref('');
const edValidade = ref('');
const edInfo = ref('');

const podeConsultar = computed(() => objPerfil.value?.ckconsultar === 'S');
const podeInserirDados = computed(() => objPerfil.value?.ckinserir_dados === 'S');
const podeAnexar = computed(() => objPerfil.value?.ckanexar_doc === 'S');
const podeAnalisarDocumentos = computed(() => objPerfil.value?.ckanalise_documentos === 'S' && objEtapa.value?.ckexige_aprovacao === 'S');
const podeAvancar = computed(() => objPerfil.value?.ckavancar === 'S' && objEtapa.value?.ckfinal === 'N');
const podeFinalizar = computed(() => objPerfil.value?.ckavancar === 'S' && objEtapa.value?.ckfinal === 'S');
const podeRetornar = computed(() => objPerfil.value?.ckretornar === 'S' && objEtapa.value?.ckpermite_retorno === 'S');

const exibirColunaAnexos = computed(() => objEtapa.value?.ckpermite_anexo === 'S' || lsAnexos.value.length > 0);
const exibirColunaFormularios = computed(() => objEtapa.value?.ckpermite_doc === 'S' || lsFormularios.value.length > 0);
const exibirColunaDocumentos = computed(() => lsDocumentos.value.length > 0);

const qtdColunasVisiveis = computed(() => {
    return [exibirColunaAnexos.value, exibirColunaFormularios.value, exibirColunaDocumentos.value]
        .filter(Boolean)
        .length || 1;
});

const resumoEtapa = computed(() => {
    const qtdDocTotal = lsFormularios.value.length;
    const qtdDocOk = lsFormularios.value.filter((item) => !!item.idlaudo).length;
    const qtdDocPendente = qtdDocTotal - qtdDocOk;

    const qtdAnexoTotal = lsAnexos.value.length;
    const qtdAnexoOk = lsAnexos.value.filter((item) => !!item.idanexo).length;
    const qtdAnexoPendente = qtdAnexoTotal - qtdAnexoOk;

    return {
        qtd_doc_total: qtdDocTotal,
        qtd_doc_ok: qtdDocOk,
        qtd_doc_pendente: qtdDocPendente,
        qtd_anexo_total: qtdAnexoTotal,
        qtd_anexo_ok: qtdAnexoOk,
        qtd_anexo_pendente: qtdAnexoPendente,
    };
});

function closeForm() {
    emit('save');
    emit('close');
}

async function lerDados() {
    try {
        msgAlerta.value = null;
        msgAlertaForm.value = null;
        isLoading.value = true;

        const req = await Api.genericPost('/dados/proc/processo', {
            idep: globalStore.idep,
            id: props.idEdicao,
        });

        objProcesso.value = req?.[0] || null;

        if (objProcesso.value.cdstatus > 3){
            msgAlerta.value = 'AENÇÃO: A edição deste processo não está mais autorizada';
            objProcesso.value = null;
        }

        if (!objProcesso.value) {
            msgAlerta.value = 'Processo não localizado.';
            return;
        }

        const rEtapa = await Api.genericPost('/dados/proc/processo/etapa', {
            idreferencia: props.idEdicao,
            id: objProcesso.value.id_etapa_atual,
        });

        objEtapa.value = rEtapa?.[0] || null;

        if (!objEtapa.value) {
            msgAlerta.value = 'Etapa atual não localizada.';
            return;
        }

        const reqPerfilEtapa = await Api.genericPost('/listas/proc/perfil/etapa', {
            idep: globalStore.idep,
            idreferencia: objEtapa.value.idmodelo_etapa,
            id: globalStore.idperfil,
        });

        objPerfil.value = reqPerfilEtapa?.[0] || null;

        if (!podeConsultar.value) {
            msgAlerta.value = 'Seu perfil não possui permissão para consultar esta etapa.';
            return;
        }

        const rDoc = await Api.genericPost('/dados/proc/processo/epdoc_from_etapa', {
            idreferencia: objEtapa.value.id,
        });
        lsFormularios.value = rDoc || [];

        const rAnexos = await Api.genericPost('/dados/proc/processo/tpdoc_from_etapa', {
            idreferencia: objEtapa.value.id,
        });
        lsAnexos.value = rAnexos || [];

        const rDocumentos = await Api.genericPost('/dados/proc/anexos', {
            idep: globalStore.idep,
            idreferencia: props.idEdicao,
            intparam1: objEtapa.value.id, // NÃO MOSTRAR NA LISTA GERAL OS DOCUMENTOS DA ETAPA ATUAL; SENÃO DUPLICA NA TELA
        });
        lsDocumentos.value = rDocumentos || [];

        await lerEtapasFluxo();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function lerEtapasFluxo() {
    try {
        etapaAnterior.value = null;
        etapaProxima.value = null;

        /*
        const req = await Api.genericPost('/dados/proc/processo/fluxo_etapa', {
            idprocesso: objProcesso.value.id,
            idetapa: objEtapa.value.id,
            idmodelo_etapa: objEtapa.value.idmodelo_etapa,
        });

        etapaAnterior.value = req?.etapaAnterior || null;
        etapaProxima.value = req?.etapaProxima || null;
        */
    } catch (error) {
        console.error(error);
    }
}

function alternarColuna(nome) {
    colunaExpandida.value = colunaExpandida.value === nome ? null : nome;
}

function colunaVisivel(nome) {
    return !colunaExpandida.value || colunaExpandida.value === nome;
}

function statusDocTexto(doc) {
    if (doc.idlaudo) return 'Concluído';
    if (doc.iddoc) return 'Em edição';
    return 'Não iniciado';
}

function statusDocClasse(doc) {
    if (doc.idlaudo) return 'ok';
    if (doc.iddoc) return 'warning';
    return 'pending';
}

function statusAnexoTexto(anexo) {
    if (anexo.anexo_nmstatus) return anexo.anexo_nmstatus;
    if (anexo.nmstatus) return anexo.nmstatus;
    if (anexo.idanexo) return 'Enviado';
    return 'Pendente';
}

function statusAnexoClasse(anexo) {
    if (anexo.idanexo) return 'ok';
    return 'pending';
}

function statusAnaliseTexto(doc) {
    if (doc.removido_dh) return 'Removido';
    if (doc.analise_dh && Number(doc.cdstatus) === 5) return 'Aprovado';
    if (doc.analise_dh && Number(doc.cdstatus) === 7) return 'Reprovado';
    if (doc.analise_dh) return 'Analisado';
    return 'Pendente';
}

function statusAnaliseClasse(doc) {
    if (doc.removido_dh) return 'pending';
    if (doc.analise_dh && Number(doc.cdstatus) === 5) return 'ok';
    if (doc.analise_dh && Number(doc.cdstatus) === 7) return 'pending';
    if (doc.analise_dh) return 'warning';
    return 'pending';
}

function podeAdicionarAnexo(anexo) {
    if (!anexo.idanexo) return true;
    return anexo.ckpermite_multiplos === 'S';
}

async function editarDocumento(doc) {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        let iddoc = doc.iddoc;

        if (!iddoc) {
            iddoc = await iniciarDocumento(doc);
            doc.iddoc = iddoc;
        }

        abrirDocEditPage(iddoc, doc);
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function iniciarDocumento(doc) {
    try {
        isLoading.value = true;
        msgAlerta.value = null;

        const payload = {
            nmrequisicao: 'SPDOC_GERAR',
            idep: globalStore.idep,
            idsim: objProcesso.value.idsim,
            idestabelecimento: objProcesso.value.idestabelecimento,
            idreferencia: doc.idepdocmodelo,
            idlogin: globalStore.idlogin,
        };

        const req = await Api.genericPost('/doc/exec_requisicao', payload);

        if (req.bolok !== 1) {
            msgAlerta.value = `ATENÇÃO: ${req.erro}`;
            return null;
        }

        const reqRegistro = await Api.genericPut('/cad/proc_processo_epdoc', {
            idetapa: objEtapa.value.id,
            iddoc: req.id,
            idmodelo_epdoc: doc.idmodelo_epdoc,
            idlogin: globalStore.idlogin,
        });

        if (reqRegistro.bolok !== 1) {
            msgAlerta.value = `ATENÇÃO: ${reqRegistro.erro}`;
            return null;
        }

        return req.id;
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
        return null;
    } finally {
        isLoading.value = false;
    }
}

function abrirDocEditPage(iddoc, doc = null) {
    console.log('Abrir DocEditPage:', iddoc, doc);
    idDocEdicao.value = iddoc;
    showDocEdit.value = true;
}


async function cancelarDocumento(doc) {
    const rModal = await modalJustificativa.value.show();

    if (!rModal.confirmado) return;

    try {
        isLoading.value = true;

        await Api.genericPut('/cad/proc_processo_epdoc', {
            id: doc.id,
            removido_idlogin: globalStore.idlogin,
            removido_motivo: rModal.justificativa,
            idlogin: globalStore.idlogin,
        });

        await lerDados();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

function abrirUpload(anexo) {
    edNomeDoc.value = anexo.nmtpdoc || '';
    edValidade.value = '';
    edInfo.value = '';
    msgAlertaForm.value = null;
    anexoUploadAtual.value = anexo;
}

function fecharUpload() {
    anexoUploadAtual.value = null;
    edNomeDoc.value = '';
    edValidade.value = '';
    edInfo.value = '';
    msgAlertaForm.value = null;
}

function getUploader() {
    return uploadModalRef.value?.getUploader?.();
}

async function salvarAnexo() {
    const anexo = anexoUploadAtual.value;
    if (!anexo) return;

    msgAlertaForm.value = null;
    const uploader = getUploader();

    let x = '';
    if (!uploader?.hasFile()) x += '\n-Necessário selecionar um arquivo';

    let r = Validar.texto(edNomeDoc.value, '\n-Informe nome ou identificação do anexo');
    if (!r.ok) x += r.msg;

    if (anexo.ckvalidade === 'S') {
        r = Validar.data(edValidade.value);
        if (!r.ok) x += '\n-Verifique a data de validade';
    }

    if (x) {
        msgAlertaForm.value = `Verifique os dados informados: ${x}`;
        return;
    }

    try {
        isLoading.value = true;

        const file = uploader.getFile();
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const payloadAnexo = {
            idep: globalStore.idep,
            idsim: objProcesso.value.idsim,
            idestabelecimento: objProcesso.value.idestabelecimento,
            idanexogrupo: 1,
            idtpdoc: anexo.idtpdoc,
            idlogin: globalStore.idlogin,
            dtvalidade: edValidade.value,
            nmdoc: edNomeDoc.value,
            obspostagem: edInfo.value,
            tipo: file.type === 'application/pdf' ? '0' : '1',
            fname: file.name,
            file_ext: ext,
            mime_type: file.type || 'application/octet-stream',
        };

        const retUpload = await uploader.upload(payloadAnexo);

        await Api.genericPut('/cad/proc_processo_tpdoc', {
            idetapa: objEtapa.value.id,
            idanexo: retUpload.id,
            idmodelo_tpdoc: anexo.idmodelo_tpdoc,
            idtpdoc: anexo.idtpdoc,
            idlogin: globalStore.idlogin,
        });

        fecharUpload();
        await lerDados();
    } catch (error) {
        msgAlertaForm.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function visualizarAnexo(idAnexo) {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        const r = await Api.genericPost('/operacoes/solicitar/download', { id: idAnexo });

        if (r.bolok === 1) {
            window.open(r.download_url, '_blank');
        }
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function removerAnexo(anexo) {
    const rModal = await modalJustificativa.value.show();

    if (!rModal.confirmado) return;

    try {
        isLoading.value = true;

        await Api.genericPut('/cad/proc_processo_tpdoc', {
            id: anexo.value.id,
            removido_idlogin: globalStore.idlogin,
            removido_motivo: rModal.justificativa,
            idlogin: globalStore.idlogin,
        });

        await lerDados();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

function abrirAnaliseDocumento(doc) {
    documentoAnaliseAtual.value = doc;
}

function fecharAnaliseDocumento() {
    documentoAnaliseAtual.value = null;
}

async function salvarAnaliseDocumento(payload) {
    try {
        isLoading.value = true;
        msgAlerta.value = null;

        let r;
        if (payload.documento.idppe){
            r = await Api.genericPut('/cad/proc_processo_epdoc', {
                id: payload.documento.idppe,
                analise_idlogin: globalStore.idlogin,
                analise_ckok: payload.cdresultado === 'A' ? 'S' : 'N',
                analise_justificativa: payload.justificativa,
                idlogin: globalStore.idlogin
            });
        } else if (payload.documento.idppt){
            r = await Api.genericPut('/cad/proc_processo_tpdoc', {
                id: payload.documento.idppt,
                analise_idlogin: globalStore.idlogin,
                analise_ckok: payload.cdresultado === 'A' ? 'S' : 'N',
                analise_justificativa: payload.justificativa,
                idlogin: globalStore.idlogin
            });
        }

        if (r?.bolok === 0) {
            msgAlerta.value = `ATENÇÃO: ${r.erro}`;
            return;
        }

        fecharAnaliseDocumento();
        await lerDados();
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

function validarPendenciasEtapa() {
    const docsObrigatoriosPendentes = lsFormularios.value.filter((doc) => {
        return doc.ckobrigatorio === 'S' && !doc.idlaudo;
    });

    const anexosObrigatoriosPendentes = lsAnexos.value.filter((anexo) => {
        return anexo.ckobrigatorio === 'S' && !anexo.idanexo;
    });

    const documentosAnalisePendentes = podeAnalisarDocumentos.value
        ? lsDocumentos.value.filter((doc) => !doc.analise_dh && !doc.removido_dh)
        : [];

    const partes = [];

    if (docsObrigatoriosPendentes.length > 0) {
        partes.push(`${docsObrigatoriosPendentes.length} formulário(s) obrigatório(s) pendente(s)`);
    }

    if (anexosObrigatoriosPendentes.length > 0) {
        partes.push(`${anexosObrigatoriosPendentes.length} anexo(s) obrigatório(s) pendente(s)`);
    }

    if (documentosAnalisePendentes.length > 0) {
        partes.push(`${documentosAnalisePendentes.length} documento(s) aguardando análise`);
    }

    if (partes.length > 0) {
        return partes.join(' e ') + '.';
    }

    return null;
}

async function avancarEtapa() {
    const pendencia = validarPendenciasEtapa();

    if (pendencia) {
        msgAlerta.value = pendencia;
        return;
    }

    const confirmado = await confirmModal.value.show({
        message: 'Deseja avançar este processo para a próxima etapa?',
        confirmText: 'Avançar',
        cancelText: 'Cancelar',
    });

    if (!confirmado) return;

    try {
        isLoading.value = true;

        const r = await Api.genericPost('/rotinas/executar', {
            nmrequisicao: 'processoAvancar',
            idep: globalStore.idep,
            idreferencia: objProcesso.value.id,
            idlogin: globalStore.idlogin,
        });

        if (r.bolok === 1) {
            closeForm();
        } else {
            msgAlerta.value = `ATENÇÃO: ${r.erro}`;
        }
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

async function retornarEtapa() {
    const rModal = await modalJustificativa.value.show();
    if (!rModal.confirmado) return;

    try {
        isLoading.value = true;

        const r = await Api.genericPost('/rotinas/executar', {
            nmrequisicao: 'processoRetornar',
            idep: globalStore.idep,
            idreferencia: objProcesso.value.id,
            idlogin: globalStore.idlogin,
            strparam1: rModal.justificativa,
        });

        if (r.bolok === 1) {
            closeForm();
        } else {
            msgAlerta.value = `ATENÇÃO: ${r.erro}`;
        }

    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
}

function formatarDataHora(valor) {
    if (!valor) return '-';
    return new Date(valor).toLocaleString('pt-BR');
}

function formatarTamanho(bytes) {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function hashResumido(hash) {
    if (!hash) return '-';
    if (hash.length <= 16) return hash;
    return `${hash.substring(0, 8)}...${hash.substring(hash.length - 8)}`;
}

onMounted(lerDados);
</script>

<style scoped>
.proc-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: 8px;
}

.proc-alert {
    margin-bottom: 0;
}

.proc-content {
    flex: 1;
    min-height: 0;
    display: grid;
    gap: 8px;
    overflow: hidden;
}

.proc-content--cols-1 {
    grid-template-columns: 1fr;
}

.proc-content--cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.proc-content--cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.proc-content--expanded {
    grid-template-columns: 1fr;
}

.proc-footer {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border: 1px solid #aaa;
    border-radius: 6px;
    padding: 7px;
}

.footer-actions {
    display: flex;
    gap: 6px;
}

.proc-btn {
    width: 82px !important;
    padding: 4px 0 !important;
    font-size: 12px !important;
}

.proc-btn.primary {
    background: #2563eb !important;
    color: #fff !important;
    box-shadow: none !important;
}

.proc-btn.warning {
    background: #f59e0b !important;
    color: #fff !important;
    box-shadow: none !important;
}

.proc-btn.danger {
    background: #dc2626 !important;
    color: #fff !important;
    box-shadow: none !important;
}

.proc-btn-toggle {
    width: 76px !important;
}

.proc-header-compact,
:deep(.proc-header-compact) {
    flex-shrink: 0;
    background: #fff;
    border: 1px solid #aaa;
    border-radius: 6px;
    padding: 7px 9px;
}

:deep(.proc-header-main) {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
}

:deep(.proc-header-info) {
    min-width: 0;
    flex: 1;
}

:deep(.proc-header-main h2) {
    margin: 0 0 2px;
    font-size: 16px;
    line-height: 1.15;
    color: #1f2937;
}

:deep(.proc-header-line) {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 12px;
    color: #444;
    line-height: 1.25;
}

:deep(.proc-header-line strong) {
    color: #222;
}

:deep(.proc-resumo-box) {
    display: flex;
    gap: 5px;
    flex-shrink: 0;
}

:deep(.proc-resumo-box div) {
    min-width: 64px;
    padding: 4px 6px;
    border-radius: 5px;
    background: #f1f5f9;
    text-align: center;
    border: 1px solid #d0d7de;
}

:deep(.proc-resumo-box strong) {
    display: block;
    font-size: 14px;
    line-height: 1;
}

:deep(.proc-resumo-box span) {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    color: #555;
}

:deep(.proc-flow-compact) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 8px;
    align-items: center;
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid #eee;
    font-size: 11px;
    color: #555;
}

:deep(.proc-flow-compact strong) {
    color: #111;
    text-align: center;
}

:deep(.proc-flow-compact span:first-child) {
    text-align: left;
}

:deep(.proc-flow-compact span:last-child) {
    text-align: right;
}

:deep(.proc-column) {
    min-height: 0;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #aaa;
    border-radius: 6px;
    padding: 7px;
}

:deep(.column-header) {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
    padding: 2px 0 6px;
    border-bottom: 1px solid #eee;
    margin-bottom: 7px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

:deep(.column-header h3) {
    margin: 0;
    font-size: 15px;
    color: #1f2937;
}

:deep(.column-header span) {
    font-size: 11px;
    color: #666;
}

:deep(.proc-card) {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 8px;
    margin-bottom: 7px;
    background: #fff;
}

:deep(.card-main) {
    display: flex;
    justify-content: space-between;
    gap: 8px;
}

:deep(.card-text) {
    min-width: 0;
}

:deep(.card-main h4) {
    margin: 0;
    font-size: 13px;
    line-height: 1.2;
    color: #1f2937;
}

:deep(.card-main p) {
    margin: 3px 0 0;
    font-size: 11px;
    color: #666;
    line-height: 1.25;
}

:deep(.status-pill) {
    white-space: nowrap;
    align-self: flex-start;
    border-radius: 999px;
    padding: 3px 7px;
    font-size: 10px;
    font-weight: 600;
    background: #eee;
}

:deep(.status-pill.ok) {
    background: #dcfce7;
    color: #14532d;
}

:deep(.status-pill.warning) {
    background: #fef3c7;
    color: #78350f;
}

:deep(.status-pill.pending) {
    background: #fee2e2;
    color: #7f1d1d;
}

:deep(.tag-row) {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    margin-top: 6px;
}

:deep(.tag) {
    font-size: 10px;
    background: #f1f5f9;
    border-radius: 999px;
    padding: 2px 6px;
    color: #334155;
}

:deep(.tag.obrigatorio) {
    background: #ffe4e6;
    color: #881337;
}

:deep(.card-actions) {
    display: flex;
    justify-content: flex-end;
    gap: 5px;
    margin-top: 7px;
    flex-wrap: wrap;
}

:deep(.empty-box) {
    color: #777;
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 6px;
    padding: 14px;
    text-align: center;
    font-size: 12px;
}

:deep(.anexo-info) {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px dashed #ddd;
    font-size: 11px;
    color: #555;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

:deep(.anexo-info strong) {
    color: #222;
}

:deep(.proc-doc-justificativa) {
    margin-top: 8px;
    padding: 8px;
    border-left: 4px solid #cbd5e1;
    background: #f8fafc;
    font-size: 12px;
    color: #334155;
    white-space: pre-line;
}

:deep(.proc-modal-upload),
:deep(.proc-modal-analise) {
    width: 720px;
    max-width: 95%;
}

:deep(.proc-modal-subtitle) {
    margin-top: -6px;
    color: #64748b;
    font-size: 13px;
}

.proc-upload-row {
    align-items: end;
}

@media (max-width: 1100px) {
    .proc-content--cols-3 {
        grid-template-columns: 1fr 1fr;
        overflow-y: auto;
    }
}

@media (max-width: 900px) {
    :deep(.proc-header-main) {
        align-items: flex-start;
        flex-direction: column;
    }

    :deep(.proc-resumo-box) {
        width: 100%;
    }

    :deep(.proc-resumo-box div) {
        flex: 1;
    }

    :deep(.proc-flow-compact) {
        grid-template-columns: 1fr;
    }

    :deep(.proc-flow-compact span),
    :deep(.proc-flow-compact strong) {
        text-align: left !important;
    }

    .proc-content,
    .proc-content--cols-2,
    .proc-content--cols-3 {
        grid-template-columns: 1fr;
        overflow-y: auto;
    }

    :deep(.proc-column) {
        overflow-y: visible;
    }
}
</style>
